import { TSelectValue } from '@models/common';
import {
  TAssetListConvertData,
  TAssetListReq,
  TAssetListState,
  TInventorySessionItem,
} from '@models/asset';
import { EUserRoles } from '@models/user';
import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import { Form, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { appToast } from '@utils/appToast';
import { getColumns } from '../columns';
import useAllDeviceApi from './useAllDeviceApi';
import { ALL_DEVICE_CONSTANT } from '../constant';

const { NAME, EXTRA_FIELD_NAME, MESSAGE } = ALL_DEVICE_CONSTANT;

const FILTER_STATE_SESSION_KEY = 'vams_pc004_filter_state';

const EMPTY_RESULT: TAssetListState = {
  totalCount: 0,
  currentPage: 1,
  pageSize: 0,
  hasNext: false,
  data: [],
  companyTotalCount: 0,
};

// --- Pure helpers extracted from buildRequestParams below to reduce its
// cyclomatic complexity. Each preserves the exact original ternary logic. ---
const toApiDateString = (value: any): string | undefined =>
  value ? dayjs(value).format('YYYY-MM-DD') : undefined;

const toApiAmount = (value?: string): number | undefined =>
  value ? Number(String(value).replace(/,/g, '')) : undefined;

const resolveHasPhotoParam = (photoAvailable?: string): boolean | undefined => {
  if (photoAvailable === 'true') {
    return true;
  }
  if (photoAvailable === 'false') {
    return false;
  }
  return undefined;
};

type TSearchFormValues = {
  companyCode?: string;
  storeCode?: string;
  assetType?: string;
  departmentName?: string;
  status?: string;
  location?: string;
  photoAvailable?: string;
  assetNo?: string;
  acquisitionDateFrom?: any;
  acquisitionDateTo?: any;
  acquisitionPriceFrom?: string;
  acquisitionPriceTo?: string;
  fiscalMonth?: string;
};

function useAllDevice() {
  const { handler, effect } = useAllDeviceApi();

  const [form] = Form.useForm();

  const {
    role,
    storeCode: ctxStoreCode,
    storeName: ctxStoreName,
  } = useContext(AppContext);
  const isStoreRole = (role ?? []).includes(EUserRoles.STORE_OWNER);

  const [companiesSelect, setCompaniesSelect] = useState<TSelectValue[]>([]);
  const [storesSelect, setStoresSelect] = useState<TSelectValue[]>([]);
  const [departmentsSelect, setDepartmentsSelect] = useState<TSelectValue[]>(
    []
  );
  const [locationsSelect, setLocationsSelect] = useState<TSelectValue[]>([]);
  const [assetTypesSelect, setAssetTypesSelect] = useState<TSelectValue[]>([]);
  const [sessionsSelect, setSessionsSelect] = useState<TInventorySessionItem[]>(
    []
  );
  const [resultData, setResultData] = useState<TAssetListState>(EMPTY_RESULT);
  const [formValues, setFormValues] = useState<TAssetListReq | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<TAssetListConvertData | null>(null);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingOther, setLoadingOther] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  const [searchWarning, setSearchWarning] = useState<string | null>(null);
  const [errorDialog, setErrorDialog] = useState<string | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const _companyCode = searchParams.get('companyCode') || '';
  // Deep-link from VAMS_PC_009 (進捗確認) [4.2] 店舗 hyperlink — carries 会社/店舗/年月 as pre-filter.
  const _storeCode = searchParams.get('storeCode') || '';
  const _inventoryYearMonth = searchParams.get('inventoryYearMonth') || '';
  // Deep-link from VAMS_PC_009 [4.10] 部門 hyperlink — carries departmentCode as extra pre-filter.
  const _departmentCode = searchParams.get('departmentCode') || '';
  const initialSearchDone = useRef(false);
  const restoredFiltersRef = useRef(false);
  const formValuesRef = useRef<TAssetListReq | null>(null);
  const storesSelectRef = useRef<TSelectValue[]>([]);
  formValuesRef.current = formValues;
  storesSelectRef.current = storesSelect;

  const handleNavigateBack = () => {
    handler.handleNavigateBack(navigate);
  };

  const saveFilterState = () => {
    sessionStorage.setItem(
      FILTER_STATE_SESSION_KEY,
      JSON.stringify({
        formFieldValues: form.getFieldsValue(),
        formValues: formValuesRef.current,
        storesSelect: storesSelectRef.current,
      })
    );
  };

  const handleRedirectPage = (record: TAssetListConvertData) => {
    saveFilterState();
    navigate(`/fixedassetledger/detail/${record.assetId}`);
  };

  const handleRedirectPhoto = (record: TAssetListConvertData) => {
    saveFilterState();
    navigate(`/fixedassetledger/photo/${record.assetId}`);
  };

  const handleNavigateToInventoryReport = () => {
    saveFilterState();
    navigate(`/fixedassetledger/report`);
  };

  const buildRequestParams = (values: TSearchFormValues): TAssetListReq => {
    return {
      inventoryYearMonth: values.fiscalMonth
        ? values.fiscalMonth.replace('/', '')
        : undefined,
      companyCode: values.companyCode || undefined,
      storeCode: values.storeCode || undefined,
      statusCodes: values.status ? [values.status] : undefined,
      departmentNames: values.departmentName
        ? [values.departmentName]
        : undefined,
      assetType: values.assetType || undefined,
      location: values.location || undefined,
      hasPhoto: resolveHasPhotoParam(values.photoAvailable),
      assetNo: values.assetNo?.trim() || undefined,
      acquisitionDateFrom: toApiDateString(values.acquisitionDateFrom),
      acquisitionDateTo: toApiDateString(values.acquisitionDateTo),
      acquisitionCostFrom: toApiAmount(values.acquisitionPriceFrom),
      acquisitionCostTo: toApiAmount(values.acquisitionPriceTo),
    };
  };

  const handleChangeTable: TableProps<TAssetListConvertData>['onChange'] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const { field, order } = sorter as SorterResult<TAssetListConvertData>;

    if (resultData.data.length && formValues) {
      const sortField = (field as string) || '';
      // Strip any previous sort params so a reset (order = null) sends no sort to the API.
      const baseParams = _.omit(formValues, ['sortBy', 'ascending']);
      const params: TAssetListReq = order
        ? {
            ...formValues,
            ...handler.buildSortParams(
              sortField.toLowerCase(),
              handler.handleSortOrder(order as string)
            ),
          }
        : baseParams;
      setFormValues(params);
      setResultData({ ...resultData, data: [] });
      handler.getAssetsSort(
        { ...params, page: 1 },
        resultData,
        setLoadingPage,
        setResultData
      );
    }
  };

  // Broken into named sub-checks (each evaluated in the same left-to-right
  // order as the original single expression) so both the function's
  // cyclomatic complexity and the number of operators in the final
  // expression stay within limits, while producing an identical result.
  // const hasBasicIdentityFilters = (values: TSearchFormValues) =>
  //   Boolean(
  //     values.companyCode ||
  //       values.storeCode ||
  //       values.status ||
  //       values.assetType ||
  //       values.departmentName
  //   );

  // const hasBasicOtherFilters = (values: TSearchFormValues) =>
  //   Boolean(values.location || values.photoAvailable || values.assetNo?.trim());

  // const hasBasicSearchFilters = (values: TSearchFormValues) =>
  //   hasBasicIdentityFilters(values) || hasBasicOtherFilters(values);

  // const hasAcquisitionRangeFilters = (values: TSearchFormValues) =>
  //   Boolean(
  //     values.acquisitionDateFrom ||
  //       values.acquisitionDateTo ||
  //       values.acquisitionPriceFrom ||
  //       values.acquisitionPriceTo
  //   );

  // const hasAnySearchCondition = (values: TSearchFormValues) =>
  //   hasBasicSearchFilters(values) || hasAcquisitionRangeFilters(values);

  const handleSearch = (values: TSearchFormValues) => {
    // if (!isStoreRole && !hasAnySearchCondition(values)) {
    //   setSearchWarning(MESSAGE.EMPTY_SEARCH_CONDITION);
    //   return;
    // }
    setSearchWarning(null);

    const params = buildRequestParams(values);

    setFormValues(params);
    setResultData(EMPTY_RESULT);
    handler.getAssetsSearch(
      { ...params, page: 1 },
      setLoadingPage,
      setResultData,
      setErrorDialog
    );
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog(null);
  };

  const handleExportData = async () => {
    if (!formValues) {
      return;
    }
    const ok = await handler.exportAssets(formValues, setLoadingExport);
    if (!ok) {
      appToast.error({ message: MESSAGE.EXPORT_ERROR });
    }
  };

  const handleRequestDelete = (record: TAssetListConvertData) => {
    setDeleteTarget(record);
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    const ok = await handler.deleteAsset(deleteTarget.assetId, setLoadingPage);
    setDeleteTarget(null);
    if (ok) {
      appToast.success({ message: MESSAGE.DELETE_SUCCESS });
      if (formValues) {
        handler.getAssetsSearch(
          { ...formValues, page: 1 },
          setLoadingPage,
          setResultData
        );
      }
    } else {
      appToast.error({ message: MESSAGE.DELETE_ERROR });
    }
  };

  const handleChangeCompany = (_val: string) => {
    // 会社 [4.1] và 店舘 [4.2] là hai bộ lọc độc lập (không còn quan hệ company→store) —
    // chọn lại 会社 không còn reset/disable/refetch danh sách 店舗 nữa.
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      !loadingPage &&
      formValues &&
      resultData.data.length < resultData.totalCount
    ) {
      handler.getAssetsScroll(
        { ...formValues, page: resultData.currentPage + 1 },
        resultData,
        setLoadingPage,
        setResultData
      );
    }
  };

  const handleSortOrderV2 = (order: string) => {
    return handler.handleSortOrderV2(order);
  };

  const columns = useMemo(() => {
    return getColumns(
      searchParams,
      handleRedirectPage,
      handleRedirectPhoto,
      handleRequestDelete,
      handleSortOrderV2
    );
  }, [searchParams]);

  // API
  useEffect(() => {
    // 会社 [4.1] always defaults to "all" regardless of role — only an explicit PC009 deep-link
    // companyCode pre-fills it, ctxCompanyCode (user's own company) no longer applies here.
    const resolvedCompanyCode = _companyCode || '';
    // companyCode is no longer guaranteed on the PC009 deep link — storeCode alone identifies it.
    const isDeepLinkInit = Boolean(_storeCode);

    // Synchronously flag restoration before any async calls so the
    // getCompanies callback won't overwrite the restored storesSelect.
    if (!isDeepLinkInit && sessionStorage.getItem(FILTER_STATE_SESSION_KEY)) {
      restoredFiltersRef.current = true;
    }

    handler.getLookups(
      setDepartmentsSelect,
      setLocationsSelect,
      setAssetTypesSelect,
      setCompaniesSelect,
      setLoadingOther,
      setLoadingPage
    );
    handler.getInventorySessions(setSessionsSelect, () => undefined);

    // 店舘 [4.2] không còn phụ thuộc 会社 — tải toàn bộ danh sách store một lần, độc lập.
    if (!isStoreRole) {
      handler.getStoresByCompany('', '', setStoresSelect, setLoadingOther);
    }

    form.setFieldValue(NAME.COMPANY, resolvedCompanyCode);
    form.setFieldValue(EXTRA_FIELD_NAME.STATUS, '');
    form.setFieldValue(EXTRA_FIELD_NAME.PHOTO_AVAILABLE, '');

    if (isStoreRole && ctxStoreCode) {
      form.setFieldValue(NAME.STORE, ctxStoreCode);
      setStoresSelect([
        {
          value: ctxStoreCode,
          label: `${ctxStoreCode}_${ctxStoreName ?? ''}`,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    if (!isStoreRole && !restoredFiltersRef.current) {
      effect.effectCompanies(form, searchParams);
    }
  }, [companiesSelect, isStoreRole]);

  // Deep-link from VAMS_PC_009 [4.2]: pre-fill 店舗 once its options for the resolved 会社 are loaded.
  useEffect(() => {
    if (!isStoreRole && !restoredFiltersRef.current && _storeCode) {
      form.setFieldValue(NAME.STORE, _storeCode);
    }
  }, [storesSelect, isStoreRole]);

  // Deep-link from VAMS_PC_009 [4.10]: pre-fill 部門 once its options are loaded.
  useEffect(() => {
    if (!isStoreRole && !restoredFiltersRef.current && _departmentCode) {
      form.setFieldValue(NAME.DEPARTMENT, _departmentCode);
    }
  }, [departmentsSelect, isStoreRole]);

  // 年月 default = kỳ mở gần nhất; 各店舗 auto-load bảng theo store mình ngay khi có kỳ mặc định,
  // 管理者/role khác giữ bảng trống tới khi search (spec §Screen Summary + [9] 表示条件: "管理者 /
  // other accounts: Asset list area is blank on initial load — data shown only after search").
  useEffect(() => {
    if (!sessionsSelect.length) {
      if (!isStoreRole) {
        setLoadingPage(false);
      }
      return;
    }

    // Deep-link from VAMS_PC_009 [4.2] takes priority over any stale saved filter state from a
    // previous PC005/006/007 visit in this browser tab — an explicit navigation with 店舗 (会社 is
    // optional/absent) must not be silently overridden by leftover sessionStorage state. 年月 is
    // best-effort (falls back to the latest open session below if it doesn't match a real one).
    const isDeepLink = Boolean(_storeCode);

    if (isDeepLink) {
      // Discard any leftover state so a later plain reload of this URL doesn't resurrect it.
      sessionStorage.removeItem(FILTER_STATE_SESSION_KEY);
    }

    // Restore filter state when returning from PC005 / PC006 / PC007
    const savedRaw = isDeepLink
      ? null
      : sessionStorage.getItem(FILTER_STATE_SESSION_KEY);
    if (savedRaw) {
      sessionStorage.removeItem(FILTER_STATE_SESSION_KEY);
      try {
        const {
          formFieldValues,
          formValues: savedFormValues,
          storesSelect: savedStoresSelect,
        } = JSON.parse(savedRaw);

        // Re-hydrate DatePicker fields that were serialised as ISO strings
        const restored = { ...formFieldValues };
        if (restored[EXTRA_FIELD_NAME.ACQ_DATE_FROM]) {
          restored[EXTRA_FIELD_NAME.ACQ_DATE_FROM] = dayjs(
            restored[EXTRA_FIELD_NAME.ACQ_DATE_FROM]
          );
        }
        if (restored[EXTRA_FIELD_NAME.ACQ_DATE_TO]) {
          restored[EXTRA_FIELD_NAME.ACQ_DATE_TO] = dayjs(
            restored[EXTRA_FIELD_NAME.ACQ_DATE_TO]
          );
        }
        restoredFiltersRef.current = true;
        form.setFieldsValue(restored);

        if (savedStoresSelect?.length) {
          setStoresSelect(savedStoresSelect);
        }

        if (savedFormValues) {
          initialSearchDone.current = true;
          setFormValues(savedFormValues);
          handler.getAssetsSearch(
            { ...savedFormValues, page: 1 },
            setLoadingPage,
            setResultData,
            setErrorDialog
          );
        } else {
          setLoadingPage(false);
        }
        return;
      } catch {
        // Fall through to normal initialisation on parse failure
      }
    }

    // Deep-link from VAMS_PC_009 [4.2]: inventoryYearMonth arrives as "YYYYMM" — this is also the
    // raw `inventoryYearMonth` sessionsSelect items use — compare directly, no slash formatting.
    // Only honor it if it matches a real session in the dropdown.
    const urlYearMonthValid = sessionsSelect.some(
      (session) => session.inventoryYearMonth === _inventoryYearMonth
    );

    const defaultYearMonth =
      (urlYearMonthValid ? _inventoryYearMonth : '') ||
      form.getFieldValue(EXTRA_FIELD_NAME.FISCAL_MONTH) ||
      sessionsSelect[0].inventoryYearMonth;
    form.setFieldValue(EXTRA_FIELD_NAME.FISCAL_MONTH, defaultYearMonth);

    if (isStoreRole && ctxStoreCode && !initialSearchDone.current) {
      initialSearchDone.current = true;
      // 会社 defaults to "all" — don't pin the auto-search to ctxCompanyCode.
      const params = buildRequestParams({
        storeCode: ctxStoreCode,
        fiscalMonth: defaultYearMonth,
      });
      setFormValues(params);
      handler.getAssetsSearch(
        { ...params, page: 1 },
        setLoadingPage,
        setResultData,
        setErrorDialog
      );
    } else if (!isStoreRole && isDeepLink && !initialSearchDone.current) {
      // Deep-link from VAMS_PC_009 [4.2]: 会社+店舗 carried over (年月 = URL value if it matches a
      // real session, otherwise the resolved default above) — show results immediately instead of
      // leaving the table blank (spec: "with 年月 and 店舗 carried over as filter").
      initialSearchDone.current = true;
      const params = buildRequestParams({
        companyCode: _companyCode,
        storeCode: _storeCode,
        departmentName: _departmentCode || undefined,
        fiscalMonth: defaultYearMonth,
      });
      setFormValues(params);
      handler.getAssetsSearch(
        { ...params, page: 1 },
        setLoadingPage,
        setResultData,
        setErrorDialog
      );
    } else if (!isStoreRole) {
      setLoadingPage(false);
    } else {
      // isStoreRole is true but there's no ctxStoreCode, or the initial
      // search already ran — no additional action needed (unchanged
      // behavior: loadingPage keeps its current value, no fetch is issued).
    }
  }, [sessionsSelect]);

  return {
    state: {
      form,
      companiesSelect,
      storesSelect,
      departmentsSelect,
      locationsSelect,
      assetTypesSelect,
      sessionsSelect,
      resultData,
      deleteTarget,
      isStoreRole,

      loadingPage,
      loadingOther,
      loadingExport,
      columns,

      searchWarning,
      errorDialog,
    },
    handler: {
      handleNavigateBack,
      handleChangeTable,
      handleSearch,
      handleScroll,
      handleChangeCompany,
      handleNavigateToInventoryReport,
      handleExportData,
      handleRequestDelete,
      handleCancelDelete,
      handleConfirmDelete,
      handleCloseErrorDialog,
    },
  };
}

export default useAllDevice;
